<template>
  <div class="pa-0 secondary--text text--darken-3">
    <!-- Premium Header Area -->
    <div class="bg-grey-light pa-8 border-bottom">
      <div class="d-flex align-center mb-2">
        <v-avatar color="primary" size="40" class="mr-4 shadow-sm">
          <v-icon color="white" small>fas fa-map-marker-alt</v-icon>
        </v-avatar>
        <div>
          <div class="text-h5 font-weight-black primary--text lh-1">
            {{ id > -1 ? 'Editar Origen' : 'Nuevo Origen' }}
          </div>
          <div class="caption grey--text font-weight-bold text-uppercase tracking-wider mt-1">
            Identificación de la Fuente del Negocio
          </div>
        </div>
      </div>
    </div>

    <div class="pa-8 bg-white">
      <ValidationObserver v-slot="{ handleSubmit }">
        <v-form @submit.prevent="handleSubmit(save)">
          
          <!-- Section: Details -->
          <div class="caption primary--text font-weight-black text-uppercase tracking-widest mb-6 d-flex align-center">
            <v-icon x-small color="primary" class="mr-2">fas fa-info-circle</v-icon> Nombre del Origen
          </div>

          <v-row dense class="mb-4">
            <v-col cols="12">
              <ValidationProvider name="Nombre" rules="required|max:200" v-slot="{ errors }">
                <v-text-field
                  v-model="item.name"
                  label="Nombre del Origen"
                  placeholder="Ej: Google Search, Redes Sociales, Recomendado"
                  outlined
                  dense
                  class="modern-input bg-white"
                  :error-messages="errors"
                  :loading="loading"
                  prepend-inner-icon="fas fa-tag"
                  counter="200"
                />
              </ValidationProvider>
            </v-col>
          </v-row>

          <v-alert
            outlined
            color="primary"
            class="modern-alert rounded-lg mt-4 pa-4 elevation-0"
            icon="fas fa-bullseye"
          >
            <div class="body-2 font-weight-bold mb-1 primary--text">Trazabilidad Comercial</div>
            <div class="caption opacity-80 primary--text">Identificar el origen de las cotizaciones permite medir la efectividad de sus campañas comerciales.</div>
          </v-alert>

          <!-- Footer Buttons -->
          <div class="d-flex mt-10 gap-3 align-center">
            <v-btn 
              v-if="id > -1"
              icon 
              color="error" 
              class="grey lighten-4 rounded-lg mr-2"
              large
              @click="showConfirmDelete = true"
              :disabled="loading"
              title="Eliminar registro"
            >
              <v-icon small>fas fa-trash-alt</v-icon>
            </v-btn>

            <v-btn 
              type="submit" 
              color="primary" 
              large 
              depressed 
              :loading="loading"
              class="flex-grow-1 py-6 rounded-lg font-weight-black text-uppercase tracking-widest shadow-md"
            >
              <v-icon small class="mr-2">fas fa-save</v-icon>
              {{ id > -1 ? 'Actualizar Datos' : 'Registrar Origen' }}
            </v-btn>

            <v-btn 
              text 
              large 
              color="grey darken-1"
              class="rounded-lg font-weight-bold px-6"
              @click.prevent="cancel"
            >
              Cancelar
            </v-btn>
          </div>
        </v-form>
      </ValidationObserver>
    </div>

    <!-- Confirm Delete -->
    <confirm
      v-model="showConfirmDelete" 
      :message="`¿Confirma eliminar el registro de origen '${item.name}'? Esta operación es irreversible.`"
      @ok="deleteRecord"
      @cancel="showConfirmDelete = false"
    />
  </div>
</template>

<script>
import { getOrigin, createOrigin, updateOrigin, deleteOrigin } from '@/api/salesNotes'
import notifications from '@/utils/notifications'

export default {
  name: 'OriginCreateUpdate',
  props: {
    id: { required: true }
  },
  data() {
    return {
      item: { id: -1, name: '' },
      loading: false,
      showConfirmDelete: false,
    }
  },
  methods: {
    async save() {
      this.loading = true
      try {
        const payload = { name: this.item.name }
        if (this.id > -1) {
          await updateOrigin(this.id, payload)
          notifications.showSuccess('Origen actualizado correctamente')
        } else {
          await createOrigin(payload)
          notifications.showSuccess('Nuevo origen registrado correctamente')
        }
        await this.$store.dispatch('getOrigins')
        this.$emit('updated')
        this.$emit('close')
      } catch (err) {
        notifications.showError('Error', 'No se pudo guardar: ' + err)
      } finally {
        this.loading = false
      }
    },
    async get() {
      if (this.id > -1) {
        this.loading = true
        try {
          const res = await getOrigin(this.id)
          this.item = res
        } catch (err) {
          notifications.showError('Error', 'No se pudieron cargar los datos')
        } finally {
          this.loading = false
        }
      } else {
        this.item = { id: -1, name: '' }
      }
    },
    async deleteRecord() {
      this.loading = true
      try {
        await deleteOrigin(this.id)
        notifications.showSuccess('Origen eliminado correctamente')
        await this.$store.dispatch('getOrigins')
        this.$emit('updated')
        this.$emit('close')
      } catch (err) {
        notifications.showError('Error', 'No se pudo eliminar: ' + err)
      } finally {
        this.loading = false
        this.showConfirmDelete = false
      }
    },
    cancel() {
      this.$emit('close')
    }
  },
  watch: {
    id: { handler: 'get', immediate: true }
  }
}
</script>

<style scoped>
.bg-grey-light { background-color: #f8fafc; }
.bg-white { background-color: white !important; }
.border-bottom { border-bottom: 1px solid rgba(0,0,0,0.06); }
.lh-1 { line-height: 1 !important; }
.tracking-widest { letter-spacing: 2px; }
.tracking-wider { letter-spacing: 1px; }
.gap-3 { gap: 12px; }
.opacity-80 { opacity: 0.8; }

.modern-input >>> .v-input__control { min-height: 48px !important; }
.modern-input >>> fieldset { border: 1px solid rgba(0,0,0,0.08) !important; border-radius: 8px !important; transition: all 0.3s ease; }
.modern-input >>> input { font-size: 0.95rem !important; font-weight: 500; }
.modern-input >>> .v-input__prepend-inner { margin-top: 12px !important; margin-right: 12px !important; }

.shadow-md { box-shadow: 0 4px 12px rgba(var(--v-primary-base), 0.2) !important; }
.shadow-sm { box-shadow: 0 2px 4px rgba(0,0,0,0.05) !important; }

.modern-alert { border-width: 1px !important; background-color: rgba(var(--v-primary-base), 0.04) !important; border-color: rgba(var(--v-primary-base), 0.1) !important; }

.opacity-70 { opacity: 0.7; }
</style>